import React from 'react'
import styled from 'styled-components'
import moment from 'moment-timezone'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import Published from 'Event/Dashboard/editor/views/Published'
import {useEditMode} from 'Event/EditModeProvider'
import {AbsoluteLink} from 'lib/ui/link/AbsoluteLink'
import {useAttendeeVariables} from 'Event'
import {AgendaSettings} from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem/AgendaList'
import {Draggable} from 'react-beautiful-dnd'
import {DraggableOverlay} from 'lib/ui/drag-and-drop'
import {useLeftyTemplate} from 'Event/template/Lefty'
import {useToggle} from 'lib/toggle'
import {AgendaItemConfig} from 'Event/template/Lefty/Dashboard/Sidebar/SidebarItem/AgendaList/AgendaItemConfig'
import {useLocalization} from 'lib/LocalizationProvider'
import {useAgendaUrl} from 'Event/Dashboard/components/agneda'

export default function AgendaItem(props: {
  agenda: AgendaSettings
  id: string
  index: number
}) {
  const {agenda, id, index} = props
  const isEditMode = useEditMode()
  const {flag: configVisible, toggle: toggleConfig} = useToggle()

  if (!isEditMode) {
    return (
      <Published component={agenda}>
        <Box aria-label="agenda">
          <Event agenda={agenda} />
          <Times agenda={agenda} />
        </Box>
      </Published>
    )
  }

  return (
    <>
      <AgendaItemConfig
        showing={configVisible}
        onClose={toggleConfig}
        agenda={agenda}
        id={id}
      />
      <Draggable draggableId={id} index={index}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <DraggableOverlay>
              <Editable
                onEdit={toggleConfig}
                key={index}
                handleProps={provided.dragHandleProps}
              >
                <>
                  <Published component={agenda}>
                    <Box aria-label="agenda">
                      <Event agenda={agenda} />
                      <Times agenda={agenda} />
                    </Box>
                  </Published>
                </>
              </Editable>
            </DraggableOverlay>
          </div>
        )}
      </Draggable>
    </>
  )
}

const Box = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[3]};
`

function Times(props: {agenda: AgendaSettings}) {
  const start = moment(props.agenda.startDate).tz(moment.tz.guess())
  const getMonth = (date: moment.Moment) => date.format('MMMM')
  const getDay = (date: moment.Moment) => date.format('Do')

  const {has12HourTime} = useLocalization()
  const timeFormat = has12HourTime ? 'h:mma' : 'HH:mm'
  const getTime = (date: moment.Moment) => date.format(timeFormat)

  const getTimezone = (date: moment.Moment) => date.format('z')
  const template = useLeftyTemplate()
  const {sidebar} = template
  const sidebarTextColor = sidebar.textColor

  const tz = getTimezone(start)

  /**
   * Single start date only
   */
  if (
    !props.agenda.endDate ||
    props.agenda.startDate === props.agenda.endDate
  ) {
    return (
      <TimeText aria-label="agenda event times" textColor={sidebarTextColor}>
        <strong>{`${getMonth(start)} ${getDay(start)}:`}</strong>{' '}
        {`${getTime(start)} ${tz}`}
      </TimeText>
    )
  }

  /**
   * Start, and end same day, different times
   */
  const end = moment(props.agenda.endDate).tz(moment.tz.guess())
  const sameMonth = getMonth(end) === getMonth(start)
  const sameDay = getDay(end) === getDay(start)
  if (sameMonth && sameDay) {
    return (
      <TimeText aria-label="agenda event times" textColor={sidebarTextColor}>
        <strong>{`${getMonth(start)} ${getDay(start)}:`}</strong>{' '}
        {getTime(start)}
        {` - ${getTime(end)} ${tz}`}
      </TimeText>
    )
  }

  /**
   * Two different date/times
   */
  return (
    <TimeText aria-label="agenda event times" textColor={sidebarTextColor}>
      <strong>{`${getMonth(start)} ${getDay(start)}:`}</strong> {getTime(start)}
      {` - `}
      <strong>
        {getMonth(end)} {getDay(end)}:
      </strong>{' '}
      {getTime(end)} {tz}
    </TimeText>
  )
}

function Event(props: {agenda: AgendaSettings}) {
  const template = useLeftyTemplate()
  const {sidebar} = template
  const v = useAttendeeVariables()
  const link = useAgendaUrl(props.agenda)

  if (link) {
    return (
      <StyledAbsoluteLink newTab to={link} textColor={sidebar.textColor}>
        <EventText aria-label="agenda event" textColor={sidebar.textColor}>
          {v(props.agenda.text)}
        </EventText>
      </StyledAbsoluteLink>
    )
  }

  return (
    <EventText aria-label="agenda event" textColor={sidebar.textColor}>
      {v(props.agenda.text)}
    </EventText>
  )
}

const TimeText = styled.span<{textColor: string | null}>`
  font-size: 14px;
  color: ${(props) => props.textColor};
`

const EventText = styled.span<{textColor: string | null}>`
  font-size: 18px;
  display: block;
  font-style: italic;
  color: ${(props) => props.textColor};
`

const StyledAbsoluteLink = styled(AbsoluteLink)<{textColor: string | null}>`
  color: ${(props) => props.textColor};

  &:hover {
    color: ${(props) => props.textColor} !important;
  }
`
